import { useState, useEffect } from 'react';
import { attendanceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Check, X, Users, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const TeamAttendance = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, record: null, type: null });
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await attendanceAPI.getManagerRequests();
      setRequests(res.data);
    } catch {
      toast.error('Failed to load team regularization requests');
    } finally {
      setLoading(false);
    }
  };

  const openAction = (record, type) => {
    setRemarks('');
    setActionDialog({ open: true, record, type });
  };

  const handleAction = async () => {
    const { record, type } = actionDialog;
    setSaving(true);
    try {
      await attendanceAPI.managerApproveRegularization(record.id, {
        status: type,
        admin_remarks: remarks || null,
      });
      toast.success(`Request ${type === 'APPROVED' ? 'approved' : 'rejected'}`);
      setActionDialog({ open: false, record: null, type: null });
      fetchRequests();
    } catch {
      toast.error('Action failed');
    } finally {
      setSaving(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return format(parseISO(iso), 'dd MMM, hh:mm a'); } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Team Attendance</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Review regularization requests from your direct reports
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Regularization Requests
            {requests.length > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800 ml-1">{requests.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
              <Users className="h-10 w-10 opacity-20" />
              <p>No pending regularization requests from your team.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Original In / Out</TableHead>
                    <TableHead>Requested In / Out</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((rec) => {
                    const reg = rec.regularization || {};
                    return (
                      <TableRow key={rec.id}>
                        <TableCell>
                          <p className="font-medium">{rec.employee_name || '—'}</p>
                        </TableCell>
                        <TableCell className="text-sm">{rec.date}</TableCell>
                        <TableCell className="text-sm">
                          <div className="text-muted-foreground">
                            <div>{fmtTime(reg.original_check_in)}</div>
                            <div>{fmtTime(reg.original_check_out)}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="text-green-700 font-medium">
                            <div>{fmtTime(reg.requested_check_in)}</div>
                            <div>{fmtTime(reg.requested_check_out)}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate" title={reg.reason}>
                          {reg.reason || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => openAction(rec, 'APPROVED')}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => openAction(rec, 'REJECTED')}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(o) => !saving && setActionDialog({ ...actionDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'APPROVED' ? 'Approve' : 'Reject'} Regularization
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'APPROVED'
                ? 'Approving this request will update the employee\'s attendance record with the requested times.'
                : 'The employee will be notified that their request was rejected.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Remarks <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              placeholder="Add a note for the employee..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, record: null, type: null })} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant={actionDialog.type === 'APPROVED' ? 'default' : 'destructive'}
              onClick={handleAction}
              disabled={saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {actionDialog.type === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamAttendance;
